import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { completeGmailConnect } from "@/lib/google-connect.functions";

const searchSchema = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
  error_description: z.string().optional(),
});

export const Route = createFileRoute("/integrations/gmail/callback")({
  head: () => ({
    meta: [
      { title: "Gmail connection — CapitalDart" },
      {
        name: "description",
        content: "Finish connecting your Gmail account to CapitalDart.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: searchSchema,
  component: GmailCallbackPage,
});

function GmailCallbackPage() {
  const { code, state, error, error_description } = Route.useSearch();
  const complete = useServerFn(completeGmailConnect);
  const [status, setStatus] = React.useState<
    { kind: "idle" } | { kind: "working" } | { kind: "ok"; email: string } | { kind: "err"; msg: string }
  >({ kind: "idle" });
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return;
    if (error) {
      started.current = true;
      setStatus({
        kind: "err",
        msg: error_description || "Google did not complete the connection.",
      });
      return;
    }
    if (!code || !state) {
      started.current = true;
      setStatus({
        kind: "err",
        msg: "This page is missing the details Google sends back. Start the connection again from Profile, App Connections.",
      });
      return;
    }
    started.current = true;
    setStatus({ kind: "working" });
    void complete({ data: { code, state } })
      .then((res) => setStatus({ kind: "ok", email: res.account_email }))
      .catch((err: unknown) =>
        setStatus({
          kind: "err",
          msg: err instanceof Error ? err.message : "Could not connect Gmail.",
        }),
      );
  }, [code, state, error, error_description, complete]);

  return (
    <main className="mx-auto max-w-[560px] px-4 pb-16 pt-12">
      <h1 className="font-serif text-[28px] text-brand-ink">Gmail connection</h1>

      {(status.kind === "idle" || status.kind === "working") && (
        <p className="mt-4 text-[14px] text-brand-muted">Finishing the connection…</p>
      )}

      {status.kind === "ok" && (
        <>
          <p className="mt-4 text-[14px] text-brand-ink">
            Gmail connected: {status.email}. You can close this tab or return to
            your dashboard.
          </p>
          <div className="mt-6">
            <Button asChild>
              <a href="/dashboard">Go to dashboard</a>
            </Button>
          </div>
        </>
      )}

      {status.kind === "err" && (
        <>
          <p className="mt-4 text-[14px] text-destructive">{status.msg}</p>
          <div className="mt-6">
            <Button asChild variant="outline">
              <a href="/profile?tab=connections">Back to App Connections</a>
            </Button>
          </div>
        </>
      )}
    </main>
  );
}
